import React from "react";
import { useLocation } from "react-router-dom";
import ErrorFallback from "./ErrorFallback";

const makeErrorId = () =>
  `ERR-${Date.now().toString(36).slice(-4)}${Math.random().toString(36).slice(2, 5)}`.toUpperCase();

class ErrorBoundary extends React.Component {
  state = { error: null, errorId: null, componentStack: "" };

  static getDerivedStateFromError(error) {
    return { error, errorId: makeErrorId() };
  }

  componentDidCatch(error, info) {
    this.setState({ componentStack: info?.componentStack || "" });
    // eslint-disable-next-line no-console
    console.error(`[${this.state.errorId}]`, error, info?.componentStack);
    if (this.props.onError) this.props.onError(error, info, this.state.errorId);
  }

  componentDidUpdate(prevProps) {
    // start fresh when `resetKey` changes (for example the route)
    if (this.state.error && prevProps.resetKey !== this.props.resetKey)
      this.reset();
  }

  reset = () =>
    this.setState({ error: null, errorId: null, componentStack: "" });

  render() {
    const { error, errorId, componentStack } = this.state;
    if (!error) return this.props.children;

    const Fallback = this.props.fallback || ErrorFallback;
    return (
      <Fallback
        error={error}
        errorId={errorId}
        componentStack={componentStack}
        reset={this.reset}
      />
    );
  }
}

/* Same boundary, but resets itself whenever the page (route) changes. Must be inside <BrowserRouter>. */
export default function RouteErrorBoundary({ children, ...props }) {
  const { pathname } = useLocation();
  return (
    <ErrorBoundary resetKey={pathname} {...props}>
      {children}
    </ErrorBoundary>
  );
}
